<script setup lang="ts">
import { computed } from 'vue';

export type BtnSize = 'xs' | 'sm' | 'md' | 'lg';
export type BtnPriority =
  | 'primary'
  | 'info'
  | 'danger'
  | 'success'
  | 'warning'
  | 'text';
export type BtnVariant = 'flat' | 'outlined' | 'text';

export interface Props {
  size?: BtnSize;
  priority?: BtnPriority;
  variant?: BtnVariant;
  icon?: string;
  appendIcon?: string;
}
const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  priority: 'primary',
  variant: 'flat',
  icon: undefined,
  appendIcon: undefined,
});

const sizeClass = computed(() => 'size-' + props.size);
const varColorClass = computed(() => 'var-color-' + props.priority);
const colorClass = computed(() => 'color-' + props.priority);
</script>

<template>
  <v-btn
    v-if="!props.icon"
    :color="$style[varColorClass]"
    :variant="props.variant"
    rounded="0"
    :append-icon="props.appendIcon"
    :class="[$style.kBtn, $style[sizeClass], $style[colorClass]]"
    data-testid="kBtn"
  >
    <div :class="$style.textItem">
      <slot>Label</slot>
    </div>
    <template v-if="!props.appendIcon" #append
      ><slot name="append"></slot
    ></template>
  </v-btn>
  <v-btn
    v-else
    :color="$style[varColorClass]"
    :variant="props.variant"
    rounded="0"
    :icon="props.icon"
    :class="[
      $style.kBtn,
      $style[sizeClass],
      $style[colorClass],
      $style.iconBtn,
    ]"
    data-testid="kBtn"
  >
  </v-btn>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  var-color-primary: colors.$color-brand-800;
  var-color-success: colors.$color-green-800;
  var-color-danger: colors.$color-red-800;
  var-color-warning: colors.$color-yellow-500;
  var-color-info: colors.$color-brand-400;
  var-color-text: colors.$color-text-primary;
}

.kBtn {
  padding: 0 16px;

  .textItem {
    letter-spacing: 0;
    font-weight: normal;
  }

  &:not([aria-expanded='true']) {
    :global {
      .v-btn__overlay {
        /* opacity: 0 !important; */
      }
    }
  }
  &:hover {
    :global {
      .v-btn__overlay {
        /* opacity: 0.2 !important; */
      }
    }
  }
  &:hover[disabled='true'],
  &:hover:disabled,
  &[disabled='true'],
  &:disabled {
    /* opacity: 0.26 !important; */
    :global {
      .v-btn__overlay {
        /* opacity: 0 !important; */
      }
    }
  }
}

.size-xs {
  height: variables.$btn-xs-height !important;
  font-size: variables.$btn-xs-font-size - 2px !important;

  .textItem {
    font-size: variables.$btn-xs-font-size;
  }
}
html .size-xs.iconBtn {
  width: variables.$btn-xs-height;
  padding: 0;
}

.size-sm {
  height: variables.$btn-sm-height !important;
  font-size: variables.$btn-sm-font-size - 2px !important;

  .textItem {
    font-size: variables.$btn-sm-font-size;
  }
}
html .size-sm.iconBtn {
  width: variables.$btn-sm-height;
  padding: 0;
}

.size-md {
  height: variables.$btn-md-height !important;
  font-size: variables.$btn-md-font-size - 2px !important;

  .textItem {
    font-size: variables.$btn-md-font-size;
    padding-top: 1px;
  }
}
html .size-md.iconBtn {
  width: variables.$btn-md-height !important;
  padding: 0;
}

.size-lg {
  height: variables.$btn-lg-height !important;
  font-size: variables.$btn-lg-font-size - 2px !important;

  .textItem {
    font-size: variables.$btn-lg-font-size;
    padding-bottom: 1px;
  }
}
html .size-lg.iconBtn {
  width: variables.$btn-lg-height !important;
  padding: 0;
}
</style>
